/**
 * Start-line band grading, LOCAL TO THE TV PAGE.
 *
 * ⚠️ This is a deliberate, reviewed COPY of the grading half of
 * `app/LagKing/src/utils/startLine.ts` — not an oversight, and not a candidate
 * for "de-duplicating" back into a cross-package import.
 *
 * The page used to reach across the repo with
 * `import { gradeStartLine, startLineCounts } from '../../../app/LagKing/src/utils/startLine.ts'`.
 * That made a desktop Electron/Vite app import a React-Native package's source
 * at runtime, so any in-flight edit to the phone app's start-line module could
 * break this build with no change here at all — and the two are worked on
 * concurrently by different people. Localizing costs ~20 lines and removes that
 * whole class of breakage.
 *
 * WHAT MUST STAY IN SYNC: the three band edges below are a PRODUCT decision, not
 * an implementation detail. They were widened 0.5/1.0/2.0 -> 0.5/1.5/2.5 on
 * 2026-08-08 because, scored against 581 measured rolls, a putt in the middle of
 * the old SLIGHT band read SLIGHT only 60% of the time. If the phone app's bands
 * ever move again, move these with them IN THE SAME CHANGE, or the TV and the
 * app will grade the same putt differently.
 *
 * The band NAMES the player sees (PURE / SLIGHT / WIDE / MAJOR) live in the app;
 * the internal grade KEYS below ('pure' | 'good' | 'off' | 'missed') are the
 * historical ones and are deliberately unchanged, because stored history and
 * every comparison keys on them.
 */

/** Holes from 20 ft. */
export const TRAINER_PURE_DEG = 0.5;
/** Holes from ~6.8 ft. */
export const TRAINER_GOOD_DEG = 1.5;
/** Holes from ~4.1 ft. */
export const TRAINER_OFF_DEG = 2.5;

export type StartLineGrade = 'pure' | 'good' | 'off' | 'missed';

export interface StartLineVerdict {
  grade: StartLineGrade;
  /** null when the putt is PURE — inside the band there is no meaningful side. */
  direction: 'left' | 'right' | null;
  /** Whether this putt extends a Start Line streak. Rides on TRAINER_GOOD_DEG. */
  counts: boolean;
}

/**
 * Behaviorally identical to the app's `gradeStartLine` for every finite input,
 * on purpose — a faithful copy is auditable, a "improved" one silently diverges.
 *
 * 🔴 Like the app's, this has NO non-finite guard: `gradeStartLine(NaN)` returns
 * a confident `{grade:'missed', direction:'right'}` for a putt the gate never
 * angled. That is why callers must check the angle is finite FIRST. Do not "fix"
 * that here by changing the return type; fix it by not calling this with NaN.
 */
export function gradeStartLine(angleDeg: number): StartLineVerdict {
  const abs = Math.abs(angleDeg);
  const direction = abs <= TRAINER_PURE_DEG ? null : angleDeg < 0 ? 'left' : 'right';
  const grade: StartLineGrade = abs <= TRAINER_PURE_DEG
    ? 'pure'
    : abs <= TRAINER_GOOD_DEG
      ? 'good'
      : abs <= TRAINER_OFF_DEG
        ? 'off'
        : 'missed';
  return { grade, direction, counts: grade === 'pure' || grade === 'good' };
}

/** The single gate for "does this putt extend the Start Line streak". */
export function startLineCounts(angleDeg: number): boolean {
  return gradeStartLine(angleDeg).counts;
}

/** Display bucket for the metrics readout, on the same band edges. */
export function startLineBucket(
  angleDeg: number,
): 'PURE' | 'SLIGHT L' | 'SLIGHT R' | 'WIDE L' | 'WIDE R' | 'MAJOR L' | 'MAJOR R' {
  const abs = Math.abs(angleDeg);
  const side = angleDeg < 0 ? 'L' : 'R';
  if (abs <= TRAINER_PURE_DEG) return 'PURE';
  if (abs <= TRAINER_GOOD_DEG) return `SLIGHT ${side}` as 'SLIGHT L' | 'SLIGHT R';
  if (abs <= TRAINER_OFF_DEG) return `WIDE ${side}` as 'WIDE L' | 'WIDE R';
  return `MAJOR ${side}` as 'MAJOR L' | 'MAJOR R';
}
